/** Money is always integer cents, USD only at launch. */

export const CURRENCY = 'USD' as const;
const LOCALE = 'en-US';

/** "$48.68" */
export function formatCents(cents: number, options?: { showCents?: boolean }): string {
  const showCents = options?.showCents ?? true;
  return new Intl.NumberFormat(LOCALE, {
    style: 'currency',
    currency: CURRENCY,
    minimumFractionDigits: showCents ? 2 : 0,
    maximumFractionDigits: showCents ? 2 : 0,
  }).format(cents / 100);
}

/** "48.68" (no symbol) — used in receipts and inputs. */
export function centsToDollarString(cents: number): string {
  return (cents / 100).toFixed(2);
}

/**
 * Parses a user-entered dollar amount ("12", "12.5", "$12.50") into cents.
 * Returns null for anything that is not a non-negative amount with ≤ 2 decimals.
 */
export function dollarsToCents(input: string | number): number | null {
  if (typeof input === 'number') {
    if (!Number.isFinite(input) || input < 0) return null;
    return Math.round(input * 100);
  }
  const cleaned = input.trim().replace(/^\$/, '').replace(/,/g, '');
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) return null;
  return Math.round(Number.parseFloat(cleaned) * 100);
}

/** 825 bps (8.25%) of 4497 → 371 cents, rounded half up. */
export function computeTaxCents(subtotalCents: number, taxRateBps: number): number {
  if (!Number.isInteger(subtotalCents) || subtotalCents < 0) {
    throw new Error('subtotalCents must be a non-negative integer');
  }
  if (!Number.isInteger(taxRateBps) || taxRateBps < 0 || taxRateBps > 100_000) {
    throw new Error('taxRateBps must be an integer between 0 and 100000');
  }
  return Math.round((subtotalCents * taxRateBps) / 10_000);
}
